<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import TopbarDropdown from './TopbarDropdown.vue'
import { useNotificationApi } from '../../../../../../Composables/Notification/useNotificationApi.ts'
import useLoading from '../../../../../../Composables/useLoading'
import type { Notification, GroupedByType } from '../../../../../../Types/notification'
import NotificationHeader from './NotificationHeader.vue'
import NotificationTabs from './NotificationTabs.vue'
import NotificationList from './NotificationList.vue'
import NotificationGroupedList from './NotificationGroupedList.vue'
import NotificationBadge from './NotificationBadge.vue'
import NotificationFooter from './NotificationFooter.vue'
import NotificationGroupSwitch from './NotificationGroupSwitch.vue'
import NotificationTabEnum from '../../../../../../Constants/NotificationTabEnum.ts'

const { fetchUnreadCount, fetchPaginatedNotifications, fetchGroupedByType } = useNotificationApi()
const { loading, setLoading } = useLoading(false)

const unreadCount = ref<number>(0)
const notifications = ref<Notification[]>([])
const groupedNotifications = ref<GroupedByType[]>([])
const activeTab = ref<string>(NotificationTabEnum.MY_TASKS)
const teamUnreadCount = ref<number>(0)
const isGroupedByType = ref<boolean>(false)
const isOpen = ref<boolean>(false)

const isTeamView = computed(() => activeTab.value === NotificationTabEnum.TEAM)
const showFooter = computed(() => !isGroupedByType.value && notifications.value.length >= 20)

async function loadData(): Promise<void> {
  setLoading(true)
  try {
    const isTeam = isTeamView.value

    if (isGroupedByType.value) {
      groupedNotifications.value = await fetchGroupedByType(isTeam)
      return
    }

    const { items } = await fetchPaginatedNotifications({
      limit: 20,
      ...(isTeam ? { team: true } : {}),
    })
    notifications.value = items
  } finally {
    setLoading(false)
  }
}

async function switchTab(tab: string): Promise<void> {
  if (activeTab.value === tab) {
    return
  }
  activeTab.value = tab
  await loadData()
}

async function fetchCounters(): Promise<void> {
  unreadCount.value = await fetchUnreadCount()
  teamUnreadCount.value = await fetchUnreadCount(true)
}

function toggleDropdown(): void {
  isOpen.value = !isOpen.value
  if (isOpen.value) {
    loadData()
  }
}

onMounted(() => {
  fetchCounters()
})
</script>

<template>
  <TopbarDropdown :model-value="isOpen">
    <template #trigger>
      <span class="topbar-btn position-relative d-inline-flex align-items-center trigger-btn" @click="toggleDropdown">
        <i class="ti-bell bell-icon"></i>
        <NotificationBadge
          :count="unreadCount"
          variant="counter"
          class="position-absolute rounded-circle d-flex align-items-center justify-content-center bell-badge"
        />
      </span>
    </template>

    <div v-if="isOpen" class="dropdown-menu p-0 dropdown-menu-right shadow-lg border-0 notification-menu show">
      <NotificationHeader :unread-count="unreadCount" />

      <NotificationTabs
        :active-tab="activeTab"
        :unread-count="unreadCount"
        :team-unread-count="teamUnreadCount"
        @switch="switchTab"
      />

      <NotificationGroupSwitch v-model="isGroupedByType" @change="loadData" />

      <div class="position-relative">
        <div v-if="loading" class="loading-overlay d-flex justify-content-center align-items-center">
          <i class="ti-reload spinner text-primary font-weight-bold fs-20"></i>
        </div>

        <div :class="{ 'loading-content': loading }">
          <NotificationGroupedList v-if="isGroupedByType" :groups="groupedNotifications" :is-team-view="isTeamView" />
          <NotificationList v-else :active-tab="activeTab" :notifications="notifications" :is-team-view="isTeamView" />
        </div>
      </div>

      <NotificationFooter :active-tab="activeTab" :show-footer="showFooter" />
    </div>
  </TopbarDropdown>
</template>

<style lang="scss" scoped>
.trigger-btn {
  cursor: pointer;
}

.bell-icon {
  font-size: 21px;
  color: #333333;
}

.bell-badge {
  top: -0.25rem;
  right: 0.35rem;
  width: 0.9rem;
  height: 0.9rem;
  font-size: 0.55rem;
  line-height: 1;
}

.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(255, 255, 255, 0.6);
  z-index: 10;
  min-height: 120px;
}

.loading-content {
  opacity: 0.4;
  pointer-events: none;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.spinner {
  animation: spin 1s linear infinite;
}

.notification-menu {
  width: 23.75rem;
  border-radius: 0.5rem !important;
  overflow: hidden;

  > * {
    border-radius: 0;
  }

  > *:first-child {
    border-top-left-radius: inherit;
    border-top-right-radius: inherit;
  }

  > *:last-child {
    border-bottom-left-radius: inherit;
    border-bottom-right-radius: inherit;
  }
}
</style>
